import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Clock,
  Users,
  MapPin,
  Video,
  Upload,
  X,
  Save,
  ChevronLeft,
  ChevronRight,
  Award,
  ExternalLink,
  Link as LinkIcon
} from "react-feather";
import { 
  collection, 
  getDocs, 
  doc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "shared/utils/firebase";
import EventAttendanceRosterModal from "./EventAttendanceRosterModal";
import EventLiveRoomModal from "./EventLiveRoomModal";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./EventsManagement.module.css";

function EventsManagement() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClub, setFilterClub] = useState("all");
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const [rosterModalEvent, setRosterModalEvent] = useState(null);
  const [liveRoomEvent, setLiveRoomEvent] = useState(null);

  const [itemForm, setItemForm] = useState({
    title: "",
    description: "",
    type: "workshop",
    club: "esd",
    date: "",
    time: "",
    endDate: "",
    endTime: "",
    duration: "",
    location: "",
    maxAttendees: "",
    instructor: "",
    speaker: "",
    price: "",
    eventMode: "offline",
    isOnline: false,
    meetingLink: "",
    registrationLink: "",
    posterUrl: "",
    status: "upcoming",
    isPublished: true,
    allowExternalRegistration: true,
    isMandatory: false,
    prizes: [],
    prizesInput: "",
    tools: [],
    toolsInput: "",
    siliconPoints: 50
  });

  const [uploadingPoster, setUploadingPoster] = useState(false);

  const handlePosterFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingPoster(true);
      const posterRef = ref(storage, `Posters/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(posterRef, file);

      uploadTask.on(
        'state_changed',
        null,
        (error) => {
          console.error("Storage upload failed, falling back to local reader:", error);
          const reader = new FileReader();
          reader.onloadend = () => {
            setItemForm(prev => ({ ...prev, posterUrl: reader.result }));
            setUploadingPoster(false);
          };
          reader.readAsDataURL(file);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setItemForm(prev => ({ ...prev, posterUrl: downloadURL }));
          setUploadingPoster(false);
        }
      );
    } catch (err) {
      console.error("Error uploading poster:", err);
      setUploadingPoster(false);
    }
  };

  const clubs = [
    { id: 'esd', name: 'Electronics System Design', shortName: 'ESD' },
    { id: 'iot', name: 'Internet of Things', shortName: 'IoT' },
    { id: 'cns', name: 'Communication & Networking', shortName: 'CNS' },
    { id: 'all', name: 'All Clubs', shortName: 'All' }
  ];

  // Combined event sub-categories (excluding exam and assignment)
  const eventSubCategories = [
    "workshop",
    "seminar", 
    "webinar",
    "conference",
    "training",
    "competition",
    "challenge",
    "bootcamp",
    "showcase",
    "meetup"
  ];

  const statuses = ["upcoming", "ongoing", "completed", "cancelled"];

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [items, searchTerm, filterType, filterStatus, filterClub]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      
      // Fetch events
      const eventsSnapshot = await getDocs(
        query(collection(db, "events"), orderBy("date", "desc"))
      );
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));

      eventsData.sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date : new Date(a.date);
        const dateB = b.date instanceof Date ? b.date : new Date(b.date);
        return dateB - dateA;
      });

      setItems(eventsData);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.instructor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.speaker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category / Type filter
    if (filterType !== "all") {
      filtered = filtered.filter(item => 
        (item.type && item.type.toLowerCase() === filterType.toLowerCase()) ||
        (item.category && item.category.toLowerCase() === filterType.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== "all") {
      const now = new Date();
      if (filterStatus === "upcoming") {
        filtered = filtered.filter(item => {
          const itemDate = item.date instanceof Date ? item.date : new Date(item.date);
          return itemDate >= now || item.status === 'upcoming';
        });
      } else if (filterStatus === "past") {
        filtered = filtered.filter(item => {
          const itemDate = item.date instanceof Date ? item.date : new Date(item.date);
          return itemDate < now;
        });
      } else if (filterStatus === "published") {
        filtered = filtered.filter(item => item.isPublished !== false);
      } else if (filterStatus === "draft") {
        filtered = filtered.filter(item => item.isPublished === false);
      } else {
        filtered = filtered.filter(item => item.status === filterStatus);
      }
    }

    // Club filter
    if (filterClub !== "all") {
      filtered = filtered.filter(item => {
        const itemClub = item.club?.toLowerCase();
        return itemClub === filterClub || itemClub === filterClub.toUpperCase();
      });
    }

    setFilteredItems(filtered);
  };

  const calculateDuration = (startDate, startTime, endDate, endTime) => {
    if (!startDate) return '';

    const start = new Date(startDate + (startTime ? `T${startTime}` : 'T00:00:00'));
    const end = endDate
      ? new Date(endDate + (endTime ? `T${endTime}` : startTime ? `T${startTime}` : 'T23:59:59'))
      : endTime
        ? new Date(startDate + `T${endTime}`)
        : null;

    if (!end || isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
      return '';
    }

    const diffMs = end.getTime() - start.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const days = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    const remainingMins = diffMinutes % 60;

    let parts = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    if (remainingHours > 0) parts.push(`${remainingHours} ${remainingHours === 1 ? 'Hour' : 'Hours'}`);
    if (remainingMins > 0 && days === 0) parts.push(`${remainingMins} Mins`);

    return parts.join(', ');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItemForm(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (['date', 'time', 'endDate', 'endTime'].includes(name)) {
        const autoDuration = calculateDuration(updated.date, updated.time, updated.endDate, updated.endTime);
        if (autoDuration) {
          updated.duration = autoDuration;
        }
      }
      return updated;
    });
  };

  const handleArrayChange = (field, value) => {
    const itemsList = value.split(',').map(item => item.trim()).filter(item => item);
    setItemForm(prev => ({ ...prev, [field]: itemsList }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventDateTime = itemForm.date && itemForm.time 
        ? new Date(itemForm.date + 'T' + itemForm.time)
        : itemForm.date ? new Date(itemForm.date) : new Date();

      const parsedPrizes = typeof itemForm.prizesInput === 'string'
        ? itemForm.prizesInput.split(',').map(t => t.trim()).filter(t => t)
        : (Array.isArray(itemForm.prizes) ? itemForm.prizes : []);

      const parsedTools = typeof itemForm.toolsInput === 'string'
        ? itemForm.toolsInput.split(',').map(t => t.trim()).filter(t => t)
        : (Array.isArray(itemForm.tools) ? itemForm.tools : []);

      const isOnline = itemForm.eventMode === 'online' || itemForm.eventMode === 'hybrid';

      const eventData = {
        title: itemForm.title,
        description: itemForm.description,
        type: itemForm.type,
        category: itemForm.type,
        club: itemForm.club,
        date: eventDateTime,
        time: itemForm.time,
        endDate: itemForm.endDate ? new Date(itemForm.endDate + (itemForm.endTime ? 'T' + itemForm.endTime : '')) : null,
        endTime: itemForm.endTime || "",
        duration: itemForm.duration,
        eventMode: itemForm.eventMode || "offline",
        location: itemForm.eventMode === 'online' ? "" : (itemForm.location || ""),
        maxAttendees: parseInt(itemForm.maxAttendees) || 0,
        instructor: itemForm.instructor || itemForm.speaker || "",
        speaker: itemForm.speaker || itemForm.instructor || "",
        price: itemForm.price,
        siliconPoints: Number(itemForm.siliconPoints) || 50,
        isOnline: isOnline,
        allowExternalRegistration: itemForm.allowExternalRegistration !== false,
        isMandatory: itemForm.isMandatory === true,
        posterUrl: itemForm.posterUrl || "",
        isPublished: itemForm.isPublished,
        prizes: parsedPrizes,
        tools: parsedTools,
        createdAt: editingItem ? editingItem.createdAt || new Date() : new Date(),
        updatedAt: new Date()
      };

      if (editingItem) {
        eventData.roomUrl = `/notice?eventId=${editingItem.id}&room=live`;
        eventData.meetingRoomId = `ROOM_${editingItem.id}`;
        await updateDoc(doc(db, "events", editingItem.id), eventData);
        setItems(items.map(item => 
          item.id === editingItem.id ? { ...item, ...eventData } : item
        ));
      } else {
        const docRef = await addDoc(collection(db, "events"), eventData);
        const finalRoomUrl = `/notice?eventId=${docRef.id}&room=live`;
        const finalRoomId = `ROOM_${docRef.id}`;
        await updateDoc(doc(db, "events", docRef.id), {
          roomUrl: finalRoomUrl,
          meetingRoomId: finalRoomId
        });
        setItems([{ id: docRef.id, ...eventData, roomUrl: finalRoomUrl, meetingRoomId: finalRoomId }, ...items]);
      }

      setShowItemModal(false);
      setEditingItem(null);
      resetForm();
    } catch (error) {
      console.error("Error saving event:", error);
    }
  };

  const parseToDate = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
    if (typeof raw.toDate === 'function') return raw.toDate();
    if (raw.seconds) return new Date(raw.seconds * 1000);
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };

  const safeFormatDateForInput = (rawDate) => {
    const d = parseToDate(rawDate);
    if (!d) return "";
    try {
      return d.toISOString().split('T')[0];
    } catch (err) {
      return "";
    }
  };

  const safeFormatTimeForInput = (item) => {
    if (item.time) return item.time;
    const d = parseToDate(item.date);
    if (!d) return "";
    try {
      return d.toTimeString().slice(0, 5);
    } catch (err) {
      return "";
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const computedMode = item.eventMode || (
      (item.isOnline && item.location) ? 'hybrid' : (item.isOnline ? 'online' : 'offline')
    );
    setItemForm({
      title: item.title || "",
      description: item.description || "",
      type: item.type || item.category || "workshop",
      club: item.club?.toLowerCase() || "esd",
      date: safeFormatDateForInput(item.date),
      time: safeFormatTimeForInput(item),
      endDate: safeFormatDateForInput(item.endDate),
      endTime: item.endTime || "",
      duration: item.duration || "",
      location: item.location || "",
      maxAttendees: item.maxAttendees || item.maxParticipants || "",
      instructor: item.instructor || "",
      speaker: item.speaker || "",
      price: item.price || "",
      siliconPoints: item.siliconPoints !== undefined ? Number(item.siliconPoints) : 50,
      eventMode: computedMode,
      isOnline: computedMode === 'online' || computedMode === 'hybrid',
      meetingLink: item.meetingLink || "",
      registrationLink: item.registrationLink || item.actionLink || "",
      posterUrl: item.posterUrl || "",
      status: item.status || "upcoming",
      isPublished: item.isPublished !== false,
      allowExternalRegistration: item.allowExternalRegistration !== false,
      isMandatory: item.isMandatory === true || item.mandatoryForAll === true,
      prizes: item.prizes || [],
      prizesInput: Array.isArray(item.prizes) ? item.prizes.join(', ') : (item.prizes || ""),
      tools: item.tools || [],
      toolsInput: Array.isArray(item.tools) ? item.tools.join(', ') : (item.tools || "")
    });
    setShowItemModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteDoc(doc(db, "events", itemToDelete.id));
      setItems(items.filter(item => item.id !== itemToDelete.id));
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const resetForm = () => {
    setItemForm({
      title: "",
      description: "",
      type: "workshop",
      club: "esd",
      date: "",
      time: "",
      endDate: "",
      endTime: "",
      duration: "",
      location: "",
      maxAttendees: "",
      instructor: "",
      speaker: "",
      price: "",
      siliconPoints: 50,
      eventMode: "offline",
      isOnline: false,
      meetingLink: "",
      registrationLink: "",
      posterUrl: "",
      status: "upcoming",
      isPublished: true,
      allowExternalRegistration: true,
      isMandatory: false,
      prizes: [],
      prizesInput: "",
      tools: [],
      toolsInput: ""
    });
  };

  const getEventTypeColor = (type) => {
    const colors = {
      'workshop': 'bg-primary',
      'seminar': 'bg-info',
      'webinar': 'bg-success',
      'conference': 'bg-warning',
      'training': 'bg-secondary',
      'competition': 'bg-danger',
      'challenge': 'bg-danger',
      'bootcamp': 'bg-primary',
      'showcase': 'bg-info',
      'meetup': 'bg-dark'
    };
    return colors[type] || 'bg-secondary';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'upcoming': return 'bg-primary';
      case 'ongoing': return 'bg-warning';
      case 'completed': return 'bg-success';
      case 'cancelled': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const getClubColor = (club) => {
    const clubLower = club?.toLowerCase();
    switch(clubLower) {
      case 'esd': return 'bg-primary';
      case 'iot': return 'bg-success';
      case 'cns': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  const getItemStatus = (item) => {
    if (!item) return { text: 'Upcoming', color: 'bg-primary' };
    if (item.isCancelled || item.status === 'cancelled') return { text: 'Cancelled', color: 'bg-danger' };
    if (item.isPostponed || item.status === 'postponed') return { text: 'Postponed', color: 'bg-warning' };

    const now = new Date();
    const startDate = parseToDate(item.date);
    if (!startDate) return { text: 'Upcoming', color: 'bg-primary' };

    if (item.time && typeof item.time === 'string') {
      const timeMatch = item.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        const ampm = timeMatch[3];
        if (ampm) {
          if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
          if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
        }
        startDate.setHours(hours, minutes, 0, 0);
      }
    }

    let endDate = parseToDate(item.endDate);
    if (!endDate) {
      endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 2);
    }

    if (now < startDate) return { text: 'Upcoming', color: 'bg-primary' };
    if (now >= startDate && now <= endDate) return { text: 'Ongoing', color: 'bg-warning' };
    return { text: 'Completed', color: 'bg-success' };
  };

  const handleToggleStatus = async (item, statusType) => {
    try {
      let updatePayload = {};

      if (statusType === 'cancel') {
        const isCurrentlyCancelled = item.status === 'cancelled' || item.isCancelled;
        updatePayload = {
          status: isCurrentlyCancelled ? 'upcoming' : 'cancelled',
          isCancelled: !isCurrentlyCancelled,
          isPostponed: false,
          updatedAt: new Date()
        };
      } else if (statusType === 'postpone') {
        const isCurrentlyPostponed = item.status === 'postponed' || item.isPostponed;
        updatePayload = {
          status: isCurrentlyPostponed ? 'upcoming' : 'postponed',
          isPostponed: !isCurrentlyPostponed,
          isCancelled: false,
          updatedAt: new Date()
        };
      }

      await updateDoc(doc(db, "events", item.id), updatePayload);

      setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...updatePayload } : i));
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const getCalendarItems = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return items.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate.getFullYear() === year && itemDate.getMonth() === month;
    });
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const calendarItems = getCalendarItems();
    const days = [];
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayItems = calendarItems.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate.toDateString() === date.toDateString();
      });
      
      days.push({ date, items: dayItems });
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const exportItems = () => {
    const csvContent = [
      ["Title", "Sub-Category", "Club", "Date", "Time", "Location", "Status"].join(","),
      ...filteredItems.map(item => [
        `"${item.title}"`,
        `"${item.type || item.category}"`,
        `"${item.club || 'All'}"`,
        `"${item.date ? item.date.toLocaleDateString() : 'N/A'}"`,
        `"${item.time || 'N/A'}"`,
        `"${item.location || (item.isOnline ? 'Online' : 'N/A')}"`,
        `"${getItemStatus(item).text}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "event-management.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading Event Management...</p>
      </div>
    );
  }

  return (
    <div className={styles.eventsManagement}>
      <PageHeader
        icon={Calendar}
        title="Event & Workshop Management"
        subtitle="Create and manage all platform events, workshops, seminars, competitions, and live sessions"
        badge={`${filteredItems.length} Events`}
        actions={
          <>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.toggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                onClick={() => setViewMode('list')}
              >
                List View
              </button>
              <button
                className={`${styles.toggleBtn} ${viewMode === 'calendar' ? styles.active : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                Calendar View
              </button>
            </div>
            <button 
              className={styles.exportBtn}
              onClick={exportItems}
            >
              <Upload size={16} className="me-2" />
              Export CSV
            </button>
            <button 
              className={styles.addBtn}
              onClick={() => {
                setEditingItem(null);
                resetForm();
                setShowItemModal(true);
              }}
            >
              <Plus size={16} className="me-2" />
              Create Event
            </button>
          </>
        }
      />

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Sub-Categories</option>
            {eventSubCategories.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="ongoing">Ongoing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filterClub}
            onChange={(e) => setFilterClub(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Clubs</option>
            {clubs.filter(c => c.id !== 'all').map(club => (
              <option key={club.id} value={club.id}>
                {club.shortName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{items.length}</div>
          <div className={styles.statLabel}>Total Events</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {items.filter(i => {
              const itemDate = i.date instanceof Date ? i.date : new Date(i.date);
              return itemDate >= new Date();
            }).length}
          </div>
          <div className={styles.statLabel}>Upcoming</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {items.filter(i => ['competition', 'challenge', 'bootcamp'].includes((i.type || i.category)?.toLowerCase())).length}
          </div>
          <div className={styles.statLabel}>Competitions & Bootcamps</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {items.filter(i => i.isOnline).length}
          </div>
          <div className={styles.statLabel}>Online Webinars</div>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className={styles.itemList}>
          {filteredItems.map((item) => (
            <div key={item.id} className={styles.itemCard}>
              <div className={styles.itemHeader}>
                <div className={styles.itemInfo}>
                  <h5 className={styles.itemTitle}>
                    {item.title}
                  </h5>
                  <div className={styles.itemMeta}>
                    <span className={`badge ${getEventTypeColor(item.type || item.category)}`}>
                      {item.type || item.category || 'event'}
                    </span>
                    <span className={`badge ${getItemStatus(item).color}`}>
                      {getItemStatus(item).text}
                    </span>
                    {item.club && (
                      <span className={`badge ${getClubColor(item.club)}`}>
                        {clubs.find(c => c.id === item.club?.toLowerCase())?.shortName || item.club}
                      </span>
                    )}
                    {(item.eventMode === 'hybrid' || (item.isOnline && item.location)) ? (
                      <span className="badge" style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}>
                        🌐 Hybrid
                      </span>
                    ) : item.isOnline ? (
                      <span className="badge bg-info">
                        <Video size={12} className="me-1" />
                        Online
                      </span>
                    ) : item.location ? (
                      <span className="badge bg-secondary">
                        🏢 Physical
                      </span>
                    ) : null}
                    {(item.isMandatory || item.mandatoryForAll) && (
                      <span className="badge" style={{ backgroundColor: '#ef4444', color: '#ffffff' }}>
                        ⭐ Mandatory for All
                      </span>
                    )}
                    <span className={`badge ${item.isPublished !== false ? 'bg-success' : 'bg-warning'}`}>
                      {item.isPublished !== false ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div className={styles.itemActions} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    className={`btn btn-sm ${item.status === 'cancelled' || item.isCancelled ? 'btn-secondary' : 'btn-outline-danger'}`}
                    onClick={() => handleToggleStatus(item, 'cancel')}
                    title={item.status === 'cancelled' || item.isCancelled ? "Re-activate Event" : "Cancel Event"}
                    style={{ fontSize: '11px', padding: '2px 8px' }}
                  >
                    {item.status === 'cancelled' || item.isCancelled ? 'Activate' : 'Cancel'}
                  </button>
                  <button
                    className={`btn btn-sm ${item.status === 'postponed' || item.isPostponed ? 'btn-secondary' : 'btn-outline-warning'}`}
                    onClick={() => handleToggleStatus(item, 'postpone')}
                    title={item.status === 'postponed' || item.isPostponed ? "Re-activate Event" : "Postpone Event"}
                    style={{ fontSize: '11px', padding: '2px 8px' }}
                  >
                    {item.status === 'postponed' || item.isPostponed ? 'Activate' : 'Postpone'}
                  </button>
                  <button
                    className={styles.actionBtn}
                    onClick={() => handleEdit(item)}
                    title="Edit Event"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={() => {
                      setItemToDelete(item);
                      setShowDeleteModal(true);
                    }}
                    title="Delete Event"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className={styles.itemDetails} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1rem' }}>
                {/* Row 1: Schedule & Location / Meeting Link */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#e0e7ff',
                      color: '#3730a3',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      fontSize: '0.83rem'
                    }}
                  >
                    <Clock size={14} style={{ color: '#4338ca' }} />
                    {parseToDate(item.date) ? parseToDate(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    {item.time ? ` • ${item.time}` : ''}
                    {parseToDate(item.endDate) ? ` to ${parseToDate(item.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${item.endTime ? ` (${item.endTime})` : ''}` : item.endTime ? ` - ${item.endTime}` : ''}
                  </span>

                  {item.location && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#4b5563' }}>
                      <MapPin size={14} style={{ color: '#ec4899' }} />
                      {item.location}
                    </span>
                  )}

                  {item.isOnline && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#4b5563', wordBreak: 'break-all' }}>
                      <Video size={14} style={{ color: '#06b6d4' }} />
                      {item.meetingLink ? `Online: ${item.meetingLink}` : 'Online Virtual Event'}
                    </span>
                  )}
                </div>

                {/* Row 2: Instructor/Speaker, Max Attendees, Price */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', fontSize: '0.85rem', color: '#4b5563' }}>
                  {(item.instructor || item.speaker) && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Users size={14} style={{ color: '#8b5cf6' }} />
                      Instructor/Speaker: <strong>{item.instructor || item.speaker}</strong>
                    </span>
                  )}

                  {(item.maxAttendees || item.maxParticipants) && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Users size={14} style={{ color: '#3b82f6' }} />
                      Max {item.maxAttendees || item.maxParticipants} participants
                    </span>
                  )}

                  {item.price && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontWeight: '700', color: '#10b981' }}>Price:</span> {
                        String(item.price).toLowerCase().includes('free')
                          ? item.price
                          : `₹${String(item.price).replace(/[\$₹]/g, '').trim()}`
                      }
                    </span>
                  )}
                </div>
              </div>

              {item.description && (
                <div className={styles.itemDescription}>
                  <p>{item.description}</p>
                </div>
              )}


              {item.prizes && item.prizes.length > 0 && (
                <div className={styles.prizesContainer}>
                  <Award size={14} className="me-1 text-warning" />
                  <span>Prizes: {Array.isArray(item.prizes) ? item.prizes.join(', ') : item.prizes}</span>
                </div>
              )}


              {item.tools && (Array.isArray(item.tools) ? item.tools.length > 0 : item.tools) && (
                <div className="mt-1 small text-muted">
                  <strong>Tools/Hardware:</strong> {Array.isArray(item.tools) ? item.tools.join(', ') : item.tools}
                </div>
              )}

              {/* Action Buttons based on event configuration */}
              <div className="mt-3 d-flex flex-wrap gap-2 justify-content-end align-items-center border-top pt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                  onClick={() => setRosterModalEvent(item)}
                  title="View Attendance Roster"
                >
                  <Users size={14} className="me-1" />
                  Attendance Roster
                </button>
                {(item.isOnline || item.eventMode === 'online' || item.eventMode === 'hybrid') && (
                  <button
                    type="button"
                    className="btn btn-sm btn-success d-inline-flex align-items-center"
                    onClick={() => setLiveRoomEvent(item)}
                    title="Host Live Virtual Room"
                  >
                    <Video size={14} className="me-1" />
                    Host Live Room 🔴
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.calendarView}>
          <div className={styles.calendarHeader}>
            <button 
              className={styles.navBtn}
              onClick={() => navigateMonth(-1)}
            >
              <ChevronLeft size={20} />
            </button>
            <h4 className={styles.calendarTitle}>
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h4>
            <button 
              className={styles.navBtn}
              onClick={() => navigateMonth(1)}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className={styles.calendarGrid}>
            {daysOfWeek.map(day => (
              <div key={day} className={styles.calendarDayHeader}>
                {day}
              </div>
            ))}
            
            {renderCalendar().map((day, index) => (
              <div 
                key={index} 
                className={`${styles.calendarDay} ${
                  day.date.getMonth() !== currentDate.getMonth() ? styles.otherMonth : ''
                } ${day.date.toDateString() === new Date().toDateString() ? styles.today : ''}`}
              >
                <div className={styles.dayNumber}>
                  {day.date.getDate()}
                </div>
                <div className={styles.dayItems}>
                  {day.items.slice(0, 2).map(item => (
                    <div 
                      key={item.id} 
                      className={`${styles.dayItem} ${getEventTypeColor(item.type || item.category)}`}
                      title={item.title}
                    >
                      {item.title}
                    </div>
                  ))}
                  {day.items.length > 2 && (
                    <div className={styles.moreItems}>
                      +{day.items.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredItems.length === 0 && (
        <div className={styles.emptyState}>
          <Calendar size={48} className="text-muted mb-3" />
          <h5>No events found</h5>
          <p className="text-muted">
            {searchTerm || filterType !== "all" || filterStatus !== "all" || filterClub !== "all"
              ? "Try adjusting your search or filters"
              : "No events have been created yet"
            }
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowItemModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Create First Event
          </button>
        </div>
      )}

      {/* Unified Event Form Modal */}
      {showItemModal && (
        <div className={styles.modalOverlay} onClick={() => setShowItemModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>
                {editingItem ? 'Edit Event' : 'Create New Event'}
              </h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowItemModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={itemForm.title}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Sub-Category / Type *</label>
                  <select
                    name="type"
                    value={itemForm.type}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {eventSubCategories.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Club Domain *</label>
                  <select
                    name="club"
                    value={itemForm.club}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {clubs.map(club => (
                      <option key={club.id} value={club.id}>
                        {club.name} ({club.shortName})
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Status (Auto-calculated)</label>
                  <input
                    type="text"
                    value={getItemStatus(itemForm).text}
                    disabled
                    className={`${styles.formInput} bg-light`}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={itemForm.date}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Start Time</label>
                  <input
                    type="time"
                    name="time"
                    value={itemForm.time}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={itemForm.endDate}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>End Time</label>
                  <input
                    type="time"
                    name="endTime"
                    value={itemForm.endTime}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>



                <div className={styles.formGroup}>
                  <label>Instructor / Speaker</label>
                  <input
                    type="text"
                    name="instructor"
                    value={itemForm.instructor}
                    onChange={handleInputChange}
                    placeholder="e.g., Dr. Smith"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Max Capacity / Attendees</label>
                  <input
                    type="number"
                    name="maxAttendees"
                    value={itemForm.maxAttendees}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Price</label>
                  <input
                    type="text"
                    name="price"
                    value={itemForm.price}
                    onChange={handleInputChange}
                    placeholder="e.g., Free, ₹199"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Silicon-Point Value</label>
                  <input
                    type="number"
                    min="0"
                    name="siliconPoints"
                    value={itemForm.siliconPoints}
                    onChange={handleInputChange}
                    placeholder="50"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                    <Users size={14} style={{ color: '#3b82f6' }} />
                    External Guest Registration
                  </label>
                  <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                    <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.86rem', color: 'var(--text-primary, #0f172a)', margin: 0 }}>
                      <input
                        type="checkbox"
                        name="allowExternalRegistration"
                        checked={itemForm.allowExternalRegistration !== false}
                        onChange={(e) => setItemForm(prev => ({ ...prev, allowExternalRegistration: e.target.checked }))}
                        style={{ width: '16px', height: '16px', accentColor: '#10b981', cursor: 'pointer' }}
                      />
                      <span>Allow Non-SiliconLogix Users</span>
                    </label>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                    <Award size={14} style={{ color: '#ef4444' }} />
                    Mandatory Event Requirement
                  </label>
                  <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                    <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.86rem', color: 'var(--text-primary, #0f172a)', margin: 0 }}>
                      <input
                        type="checkbox"
                        name="isMandatory"
                        checked={itemForm.isMandatory === true}
                        onChange={(e) => setItemForm(prev => ({ ...prev, isMandatory: e.target.checked }))}
                        style={{ width: '16px', height: '16px', accentColor: '#ef4444', cursor: 'pointer' }}
                      />
                      <span>Mandatory for All SiliconLogix Users</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Description *</label>
                <textarea
                  name="description"
                  value={itemForm.description}
                  onChange={handleInputChange}
                  placeholder="Detailed explanation of the event..."
                  required
                  rows="4"
                  className={styles.formInput}
                />
              </div>

              {/* Minimal Radio Event Mode Selection (Right After Description) */}
              <div className={styles.eventModeCard}>
                <label className={styles.modeSectionLabel}>Event Delivery Mode *</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="eventMode"
                      value="offline"
                      checked={itemForm.eventMode === 'offline'}
                      onChange={() => setItemForm(prev => ({ ...prev, eventMode: 'offline' }))}
                      className={styles.radioInput}
                    />
                    <span>Offline (Physical)</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="eventMode"
                      value="online"
                      checked={itemForm.eventMode === 'online'}
                      onChange={() => setItemForm(prev => ({ ...prev, eventMode: 'online' }))}
                      className={styles.radioInput}
                    />
                    <span>Online (Virtual)</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="eventMode"
                      value="hybrid"
                      checked={itemForm.eventMode === 'hybrid'}
                      onChange={() => setItemForm(prev => ({ ...prev, eventMode: 'hybrid' }))}
                      className={styles.radioInput}
                    />
                    <span>Hybrid (Both)</span>
                  </label>
                </div>

                {/* Conditional Location & Auto-Generated Meeting Link Section */}
                {(itemForm.eventMode === 'offline' || itemForm.eventMode === 'hybrid') && (
                  <div className={styles.formGroup} style={{ marginBottom: itemForm.eventMode === 'hybrid' ? '1rem' : '0px' }}>
                    <label>Location (Physical Venue)</label>
                    <input
                      type="text"
                      name="location"
                      value={itemForm.location}
                      onChange={handleInputChange}
                      placeholder="e.g., ESD Lab 201, Silicon Main Auditorium"
                      className={styles.formInput}
                    />
                  </div>
                )}

                {(itemForm.eventMode === 'online' || itemForm.eventMode === 'hybrid') && (
                  <div style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '12px 16px', borderRadius: '10px', marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#059669', fontWeight: '700', fontSize: '0.88rem', marginBottom: '6px' }}>
                      <Video size={16} />
                      <span>Auto-Generated Native Meeting Room Active</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '4px' }}>
                      Event Room ID: <code style={{ backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#0284c7', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>EventRoom_{editingItem?.id || 'AUTO_GENERATED'}</code>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      Native Room URL: <strong style={{ color: '#0f172a', wordBreak: 'break-all' }}>/notice?eventId={editingItem?.id || 'AUTO_GENERATED'}&room=live</strong>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Prizes (comma-separated, if competition)</label>
                <input
                  type="text"
                  name="prizesInput"
                  value={itemForm.prizesInput !== undefined ? itemForm.prizesInput : (Array.isArray(itemForm.prizes) ? itemForm.prizes.join(', ') : "")}
                  onChange={handleInputChange}
                  placeholder="e.g., 1st Prize ₹5000, Certificate"
                  className={styles.formInput}
                />
              </div>

              {/* Event Poster / Info Document Section */}
              <div className={styles.formGroup}>
                <label>Event Poster / Info Document (JPG / Image)</label>
                <div className={styles.posterUploadGroup}>
                  <input
                    type="text"
                    name="posterUrl"
                    value={itemForm.posterUrl}
                    onChange={handleInputChange}
                    placeholder="Paste image URL (e.g. https://.../poster.jpg)"
                    className={styles.formInput}
                    style={{ marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Or upload image file:</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={handlePosterFileChange}
                      style={{ fontSize: '0.85rem' }}
                    />
                    {uploadingPoster && (
                      <span className="small text-primary spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </span>
                    )}
                  </div>
                  {itemForm.posterUrl && (
                    <div className={styles.posterPreview}>
                      <img src={itemForm.posterUrl} alt="Event Poster Preview" className={styles.posterImg} />
                      <button
                        type="button"
                        className={styles.removePosterBtn}
                        onClick={() => setItemForm(prev => ({ ...prev, posterUrl: "" }))}
                        title="Remove Poster"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Tools / Hardware Required (comma-separated)</label>
                <input
                  type="text"
                  name="toolsInput"
                  value={itemForm.toolsInput !== undefined ? itemForm.toolsInput : (Array.isArray(itemForm.tools) ? itemForm.tools.join(', ') : "")}
                  onChange={handleInputChange}
                  placeholder="e.g., Vivado 2023.2, FPGA Board, Oscilloscope"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={itemForm.isPublished}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span>Publish Event to Noticeboard</span>
                </label>
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowItemModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <Save size={16} className="me-2" />
                  {editingItem ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Confirm Deletion</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete the event <strong>"{itemToDelete.title}"</strong>?
              </p>
              <p className="text-danger">
                This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Attendance Roster Modal */}
      {rosterModalEvent && (
        <EventAttendanceRosterModal
          event={rosterModalEvent}
          onClose={() => setRosterModalEvent(null)}
        />
      )}

      {/* Admin Live Room Host Modal */}
      {liveRoomEvent && (
        <EventLiveRoomModal
          event={liveRoomEvent}
          onClose={() => setLiveRoomEvent(null)}
        />
      )}
    </div>
  );
}

export default EventsManagement;
